import React, { useCallback, useEffect } from 'react';
import AccessibleDiv from 'components/AccessibleDiv/AccessibleDiv';
import { ToolbarDropdownType } from 'components/EditorToolbar/constants';
import Icon from 'components/Icon';
import { IconType } from 'components/Icon/constants';
import { TitleWithNamespace as Title } from 'components/Toolbar/constants';
import StyleDropdown from 'components/Toolbar/Dropdowns/BrandStyle';
import { CHANGE_MARK } from 'const';
import { BrandStyleMap } from 'models';
import { intlGet } from 'utils/intlGet';
import { IBrandStyleControlProps } from './models';
import styles from './styles.module.scss';

const BrandStyleControl: React.FunctionComponent<IBrandStyleControlProps> = (props: IBrandStyleControlProps) => {
  const icon = React.createRef<HTMLDivElement>();
  const {
    activeDropdown,
    closeDialog,
    brandStyle,
    brandStyleChanged,
    sectionStyles,
    wrapperOnMouseDownHackCallback,
    setStyle,
    returnFocusToEditor,
    dataTestId = 'brandStyleControl',
  } = props;
  const defaultText = intlGet('EditorToolbar.StyleDropdown', 'Default');
  const showDropdown = activeDropdown === ToolbarDropdownType.BRAND_STYLE;
  const changeMark = <span className={styles.changeMark}>{CHANGE_MARK}</span>;

  const handleStyleSelection = useCallback((style: BrandStyleMap): void => {
    setStyle(style);
    returnFocusToEditor();
  }, [setStyle, returnFocusToEditor]);

  const closeDialogHandler = (event: MouseEvent): void => {
    closeDialog(event, ToolbarDropdownType.BRAND_STYLE, Boolean(icon?.current?.contains(event.target as Node)));
  };

  useEffect(() => {
    document.addEventListener('click', closeDialogHandler);

    return (): void => document?.removeEventListener('click', closeDialogHandler);
  }, [activeDropdown]);

  return (
    <div className={styles.BrandStyleControl} data-title={Title.TEXT_STYLE} data-testid={dataTestId}>
      <AccessibleDiv
        onMouseDown={(e): void => wrapperOnMouseDownHackCallback(e, ToolbarDropdownType.BRAND_STYLE)}
        ref={icon}
        className={styles.styleControl}
        data-tooltip={brandStyle ? brandStyle.get('name') : intlGet('EditorToolbar.Tooltip', 'FontStyle')}
        data-testid={`${dataTestId}-button`}
      >
        <span className={styles.controlText}>
          <span className={styles.brandStyle} data-testid={`${dataTestId}-brandStyle`}>{brandStyle ? brandStyle.get('name') : defaultText}</span>
          {brandStyle && brandStyleChanged && changeMark}
        </span>
        <Icon type={IconType.CHEVRON_DOWN} size="badge" color={showDropdown ? 'primary' : 'secondary'} hoverColor="primary-hover" />
      </AccessibleDiv>
      {
        showDropdown &&
        <StyleDropdown
          activeBrandStyleId={brandStyle?.get('id')}
          toggleBrandStyle={handleStyleSelection}
          sectionStyles={sectionStyles}
          dataTestId={`${dataTestId}-styleDropdown`}
        />
      }
    </div>
  );
};

export default BrandStyleControl;
